import { useMemo } from 'react';

import { WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useCustomLatestCompareToVersions } from 'hooks/useCustomLatestCompareToVersions';
import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useQueryGetLogicCheckerCategoryResults } from 'api/queries/getLogicCheckerCategoryResults.query';
import { useQueryGetLogicCheckerConfiguration } from 'api/queries/getLogicCheckerConfiguration.query';
import { useQueryGetValidationTypeDrillDown } from 'api/queries/getValidationTypeDrillDown.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';

import { getCheck } from 'utilitys/logicQualityCheck.utils';

const SEARCH_CATEGORY_NAME = 'All';

export type UseLogicQualitySingleCheckProvider = ReturnType<typeof useLogicQualitySingleCheckProvider>;

export const useLogicQualitySingleCheckProvider = ({
    widgetViewMode,
    localFilters,
    globalFilterData,
    latestVersionId,
    check,
    widgetId,
    projectId,
    contractId,
    componentKey,
    endpoints,
}) => {
    const source = WidgetViewModeUtils.isReportView(widgetViewMode) ? localFilters : globalFilterData;

    const filters = useMemo(
        () =>
            getFiltersData({
                [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                    ?.filter((item) => !item.isExclude)
                    .map((i) => i.name),
                [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                    ?.filter((item) => item.isExclude)
                    .map((i) => i.name),
                [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
                [FilterNames.TAG_CARD_BOARD]: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
                [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
                [FilterNames.WBS_SEARCH]: source?.wbs,
            }),

        [source?.activityCodes, source?.milestoneFragnet, source?.tags, source?.wbs],
    );

    // Get latest version hashcode for full reroute in table links
    const { latestVersion } = useCustomLatestCompareToVersions({
        projectId: projectId,
        contractId: contractId,
        componentKey: componentKey,
    });

    const getLogicCheckerCategoryResultsPayload = {
        fileMetaDataId: latestVersionId,
        filter: {
            filters,
        },
    };

    //All checks per categories
    const getLogicCheckerCategoryResults = useQueryGetLogicCheckerCategoryResults(
        getLogicCheckerCategoryResultsPayload,
        endpoints.getLogicCheckerCategoryResults,
    );

    //Search in "All" category, and find the specific check by validationName
    const pickedCheck = getCheck({
        categoriesSummaryRows: getLogicCheckerCategoryResults.data?.categoriesSummaryRows,
        parsedCategory: SEARCH_CATEGORY_NAME,
        parsedCheck: check,
    });

    //Pills
    const pillsData = {
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
        tagsExclude: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
        activityCodes: source?.activityCodes?.filter((item) => !item.isExclude).map((i) => i.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.isExclude).map((i) => i.name),
        wbs: source?.wbs,
    };
    const { pillsList: pills } = useGlobalFiltersHook({ source: pillsData, latestVersionId, widgetId });

    return {
        ui: {
            state: {
                tags: source?.tags,
                wbs: source?.wbs,
                activityCodes: source?.activityCodes,
                milestoneFragnet: source?.milestoneFragnet,
            },
            pills,
            vars: {
                threshold: pickedCheck?.threshold,
                latestVersionHashCode: latestVersion?.versionHashCode,
            },
        },
        api: {
            logicCheckerConfiguration: useQueryGetLogicCheckerConfiguration({ contractId }),
            validationTypeDrillDown: useQueryGetValidationTypeDrillDown({
                fileMetaDataId: latestVersionId,
                componentId: pickedCheck?.validationId,
                filter: {
                    filters: filters,
                },
            }),
            getLogicCheckerCategoryResults: {
                instance: getLogicCheckerCategoryResults,
                payload: getLogicCheckerCategoryResultsPayload,
                endpoint: endpoints.getLogicCheckerCategoryResults,
            },
        },
    };
};
